import _ from 'lodash'

export class ImmutableOps<T extends object> {

	private readonly obj: T;

	/**
	 * Creates ImmutableOps from the given object
	 */
	static for<E extends object>(obj: E): ImmutableOps<E> {
		return new ImmutableOps(obj);
	}

	/**
	 * Creates a deep copy of the given object
	 */
	static objectFrom<E extends object>(obj: E): E {
		return ImmutableOps.for(obj).obj;
	}

	private constructor(obj: T) {
		this.obj = _.cloneDeep(obj);
	}

	/**
	 * Sets the value at path of object. Value is deeply copied before it is set.
	 */
	set(path: string, value: any): ImmutableOps<T> {
		_.set(this.obj, path, _.cloneDeep(value));
		return this;
	}

	/**
	 * Sets the value at path of object only if the given condition returns true. Value is deeply copied before it is set.
	 */
	setIf(condition: () => boolean, path: string, value: any) {
		return condition() ? this.set(path, value) : this;
	}

	/**
	 * Executes given ops on the underlying object only if passed condition returns true.
	 */
	performIf(condition: () => boolean, ops: (obj: T) => ImmutableOps<T>): ImmutableOps<T> {
		if (condition()) {
			return ops(this.obj);
		}
		return this;
	}

	/**
	 * Creates an immutable ops for array at path. All subsequent operations will be performed on this array. If the array is null, an error is thrown, unless createArrayIfNull = true.
	 */
	arrayOps(path: string, createArrayIfNull?: boolean): ArrayOps<T> {
		return new ArrayOps(this, path, createArrayIfNull);
	}

	/**
	 * Returns deep copy of the underlying object.
	 */
	getObj(): T {
		return ImmutableOps.objectFrom(this.obj);
	}
}

class ArrayOps<T extends object> {
	private array: any[];

	constructor(private immutable: ImmutableOps<T>, private path: string, createArrayIfNull?: boolean) {
		let maybeArray = _.get(this.immutable.getObj(), path);
		if (maybeArray == null) {
			if (createArrayIfNull) {
				maybeArray = [];
			} else {
				throw new Error(`${path} is null`);
			}
		} else if (!_.isArray(maybeArray)) {
			throw new Error(`${path} is not an array`);
		}

		this.array = maybeArray as any[];
	}

	/**
	 * Push the given item at the ned of array.
	 */
	push(item: any): ArrayOps<T> {
		this.array.push(ImmutableOps.objectFrom(item));
		return this;
	}

	sort(comparator: (p1, p2) => number): ArrayOps<T> {
		this.array.sort(comparator);
		return this;
	}

	/**
	 * Removes from array all items that meets the given condition
	 */
	drop(condition: (val: any) => boolean): ArrayOps<T> {
		this.array = this.array.filter(item => !condition(item));
		return this;
	}

	getObj(): T {
		let obj = this.immutable.getObj();
		_.set(obj, this.path, ImmutableOps.objectFrom(this.array));
		return obj;
	}

	/**
	 * Checks if the item exists in the array using provided 'itemSelector'. If exists, it is updated with 'update' function, otherwise new item is created with 'createItem' fcn and pushed at the end of array.
	 */
	updateOrPush(itemSelector: (item: any) => boolean, update: (item: any) => void, createItem: () => any): ArrayOps<T> {
		let item = this.array.find(itemSelector);
		if (item) {
			update(item);
		} else {
			this.array.push(createItem());
		}
		return this;
	}

	/**
	 * Checks if the item exists in the array using provided 'itemSelector'. If exists, it is updated with 'update' function.
	 */
	updateIfExist(itemSelector: (item: any) => boolean, update: (item: any) => void): ArrayOps<T> {
		let item = this.array.find(itemSelector);
		if (item) {
			update(item);
		}
		return this;
	}

	/**
	 * Invokes 'update' fcn on all items in the array.
	 */
	updateAll(update: (item: any) => void): ArrayOps<T> {
		return this.updateIfExist(() => true, update);
	}

	/**
	 * Invokes 'update' fcn on item at the given index
	 */
	updateItemAtIndex(index: number, update: (item: any) => void): ArrayOps<T> {
		update(this.array[index]);
		return this;
	}

	concat(otherArray: any[]) : ArrayOps<T>{
		this.array = this.array.concat(ImmutableOps.for(otherArray).getObj());
		return this;
	}

	/**
	 * Executes the given immutable operations on those items in the array for which 'itemSelector' returns true.
	 */
	performOnItems<E extends object>(itemSelector: (item: E) => boolean, ops: (item: E) => ImmutableOps<E> | ArrayOps<E>): ArrayOps<T> {
		this.array = this.array.map(item => {
			if (itemSelector(item)) {
				return ops(item).getObj();
			} else {
				return item;
			}
		});

		return this;
	}

	/**
	 * Executes the given immutable operations on all items in the array.
	 * @param ops
	 */
	performForEach<E extends object>(ops: (item: E) => ImmutableOps<E> | ArrayOps<E>): ArrayOps<T> {
		return this.performOnItems(() => true, ops);
	}
}
